import React, { useCallback, useEffect, useState } from 'react';
import { Loader2, RefreshCw, Server } from 'lucide-react';
import { settingsApi, titanApi, ApiError } from '../../lib/api';
import { Button } from '../ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';

/**
 * Sprint 7 — read-only view of platform system settings + API health (admin).
 * Sensitive key values are not listed here; use Secret Manager / GCP console for secrets.
 */
const IntegrationsTab: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);
  const [system, setSystem] = useState<Record<string, unknown> | null>(null);
  const [health, setHealth] = useState<Record<string, unknown> | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setErr(null);
    try {
      const [cfg, h] = await Promise.all([
        settingsApi.getConfigs() as unknown as Record<string, unknown>,
        titanApi.health().catch(() => null),
      ]);
      setSystem(cfg);
      setHealth(h as Record<string, unknown> | null);
    } catch (e) {
      setErr(e instanceof ApiError ? e.detail : 'Failed to load integrations');
      setSystem(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const risk = (system?.risk || {}) as Record<string, unknown>;
  const sys = (system?.system || {}) as Record<string, unknown>;

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-lg font-semibold flex items-center gap-2">
            <Server className="w-5 h-5" />
            Platform integrations
          </h2>
          <p className="text-sm text-muted-foreground mt-1 max-w-2xl">
            High-level system flags from <code className="text-xs">/system/configs</code> and live API health. Key
            material lives in Secret Manager; traders configure brokers under Settings.
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={() => void load()} disabled={loading}>
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
          <span className="ml-2">Refresh</span>
        </Button>
      </div>

      {err && (
        <div className="rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-sm text-destructive">
          {err}
        </div>
      )}

      {loading && !system ? (
        <div className="flex justify-center py-12 text-muted-foreground gap-2 text-sm">
          <Loader2 className="w-5 h-5 animate-spin" /> Loading…
        </div>
      ) : null}

      {system && (
        <div className="grid gap-4 md:grid-cols-2">
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm">Risk (system config)</CardTitle>
              <CardDescription className="text-xs">Kelly, sizing, drawdown caps as persisted.</CardDescription>
            </CardHeader>
            <CardContent className="text-xs font-mono text-muted-foreground space-y-1">
              <div>kelly_fraction: {String(risk.kelly_fraction ?? '—')}</div>
              <div>max_position_pct: {String(risk.max_position_pct ?? '—')}</div>
              <div>max_drawdown: {String(risk.max_drawdown ?? '—')}</div>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm">Execution &amp; regime</CardTitle>
              <CardDescription className="text-xs">Platform-wide execution mode and regime hint.</CardDescription>
            </CardHeader>
            <CardContent className="text-xs font-mono text-muted-foreground space-y-1">
              <div>execution_mode: {String(sys.execution_mode ?? '—')}</div>
              <div>market_regime: {String(sys.market_regime ?? '—')}</div>
              <div>kill_switch_enabled: {String(sys.kill_switch_enabled ?? '—')}</div>
            </CardContent>
          </Card>
        </div>
      )}

      {health && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm">API health snapshot</CardTitle>
          </CardHeader>
          <CardContent className="text-xs font-mono overflow-auto max-h-48 text-muted-foreground">
            <pre>{JSON.stringify(health, null, 2)}</pre>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default IntegrationsTab;
